import { reportInfo } from '@ms/yammer-telemetry';
import type { RequestInitiator } from '@ms/yammer-telemetry-support';

import { getExperimentTreatmentIfActive } from '../domains/experiments/selectors';
import { isFeatureRolloutEnabled } from '../domains/featureRollouts/selectors';
import { experimentConfigs } from '../state/ecsExperiments/types';
import type { EcsProjectTeamName, Experiment, FeatureRollout, YammerState } from '../state/types';

import { executeRequest, getResponseBody } from './apiRequest';
import { GraphqlApiError, isFieldScopedGraphqlError } from './errors';
import type {
  ApiHeaders,
  ApiRequest,
  ApiResponse,
  GraphqlApiRequest,
  GraphqlApiRequestBody,
  GraphqlApiTreatmentRecord,
  GraphqlError,
} from './types';

const addTreatmentsToBody = <T>(
  body: GraphqlApiRequestBody<T>,
  treatments?: GraphqlApiTreatmentRecord[]
): GraphqlApiRequestBody<T> => {
  if (treatments && treatments.length > 0) {
    const existingTreatments = body.extensions?.yammerTreatments?.treatments ?? [];

    return {
      ...body,
      extensions: {
        ...body.extensions,
        yammerTreatments: {
          version: '1',
          treatments: [...existingTreatments, ...treatments],
        },
      },
    };
  }

  return body;
};

export interface AddTreatmentsToRequestOptions<T> {
  readonly request: GraphqlApiRequest<T>;
  readonly treatments?: GraphqlApiTreatmentRecord[];
}
type AddTreatmentsToRequest = <T>(options: AddTreatmentsToRequestOptions<T>) => GraphqlApiRequest<T>;
export const addTreatmentsToRequest: AddTreatmentsToRequest = ({ request, treatments }) => ({
  ...request,
  body: addTreatmentsToBody(request.body, treatments),
});

export interface GetGraphqlApiRequestOptions<T> {
  readonly body: GraphqlApiRequestBody<T>;
  readonly initiator?: RequestInitiator;
  readonly treatments?: GraphqlApiTreatmentRecord[];
  readonly headers?: ApiHeaders;
  readonly tolerateFieldScopedErrors?: boolean;
}
type GetGraphqlApiRequest = <T>(options: GetGraphqlApiRequestOptions<T>) => GraphqlApiRequest<T>;
export const getGraphqlApiRequest: GetGraphqlApiRequest = ({
  body,
  initiator,
  treatments,
  headers,
  tolerateFieldScopedErrors,
}) => ({
  path: '/graphql',
  method: 'POST',
  body: addTreatmentsToBody(body, treatments),
  apiName: body.operationName,
  requestType: 'Graphql',
  credentials: 'omit',
  initiator,
  headers,
  ...(tolerateFieldScopedErrors === true ? { tolerateFieldScopedErrors } : {}),
});

interface GetTreatmentsExtensionForGraphqlBodyOptions {
  readonly state: YammerState;
  readonly experiments?: Experiment[];
  readonly featureRollouts?: FeatureRollout[];
}
type GetTreatmentsExtensionForGraphqlBody = (
  options: GetTreatmentsExtensionForGraphqlBodyOptions
) => GraphqlApiTreatmentRecord[];
export const getTreatmentsExtensionForGraphqlBody: GetTreatmentsExtensionForGraphqlBody = ({
  state,
  experiments = [],
  featureRollouts = [],
}) => {
  const experimentTreatmentRecords: GraphqlApiTreatmentRecord[] = experiments
    .map((experiment) => ({
      project: experimentConfigs[experiment].projectTeam,
      key: experiment,
      value: getExperimentTreatmentIfActive(state, experiment)?.toString() || '',
    }))
    .filter((experiment) => experiment.value);

  const project: EcsProjectTeamName = 'YammerClients';
  const featureRolloutTreatmentRecords: GraphqlApiTreatmentRecord[] = featureRollouts
    .map((featureRollout) => ({
      project,
      key: featureRollout,
      value: isFeatureRolloutEnabled(state, featureRollout) ? 'true' : '',
    }))
    .filter((featureRollout) => featureRollout.value);

  return [...experimentTreatmentRecords, ...featureRolloutTreatmentRecords];
};

type ExecutedGraphqlApiRequest = ApiRequest & Pick<GraphqlApiRequest, 'apiName' | 'tolerateFieldScopedErrors'>;

type HasUsableRootField = (data: unknown) => boolean;
const hasUsableRootField: HasUsableRootField = (data) =>
  typeof data === 'object' && data != null && Object.values(data).some((value) => value != null);

interface GraphqlResponseBody {
  readonly data?: unknown;
  readonly errors?: unknown;
}

type IsGraphqlResponseBody = (body: unknown) => body is GraphqlResponseBody;
const isGraphqlResponseBody: IsGraphqlResponseBody = (body) => typeof body === 'object' && body != null;

type IsGraphqlErrorArray = (errors: unknown) => errors is GraphqlError[];
const isGraphqlErrorArray: IsGraphqlErrorArray = (errors): errors is GraphqlError[] =>
  Array.isArray(errors) && errors.every((error) => typeof error === 'object' && error != null);

type ReportToleratedFieldScopedErrors = (request: ExecutedGraphqlApiRequest, errors: GraphqlError[]) => void;
const reportToleratedFieldScopedErrors: ReportToleratedFieldScopedErrors = (request, errors) => {
  const [firstError] = errors;

  reportInfo({
    eventName: 'graphql_field_scoped_errors_tolerated',
    eventProperties: {
      name: request.apiName,
      resultCount: errors.length,
      reason: errors.map((error) => error.path?.join('.')).join(', '),
      errorCode: firstError.extensions?.code,
      category: firstError.extensions?.category,
      correlationId: firstError.correlationId,
    },
  });
};

type ProcessGraphqlErrors = (
  request: ExecutedGraphqlApiRequest,
  body: unknown,
  response: Response
) => GraphqlResponseBody | undefined;
const processGraphqlErrors: ProcessGraphqlErrors = (request, body, response) => {
  if (!isGraphqlResponseBody(body)) {
    return undefined;
  }

  const graphqlResponseBody = body;
  const { data, errors: graphqlErrors } = graphqlResponseBody;

  if (!Object.prototype.hasOwnProperty.call(graphqlResponseBody, 'errors')) {
    return undefined;
  }

  if (!isGraphqlErrorArray(graphqlErrors)) {
    throw new GraphqlApiError(request, 'Invalid GraphQL error response', response.status, response.headers);
  }

  if (graphqlErrors.length === 0) {
    return undefined;
  }

  const hasUsableData = hasUsableRootField(data);
  const isEveryErrorFieldScoped = graphqlErrors.every(isFieldScopedGraphqlError);
  const shouldTolerateErrors =
    request.tolerateFieldScopedErrors === true && response.ok && hasUsableData && isEveryErrorFieldScoped;

  if (shouldTolerateErrors) {
    reportToleratedFieldScopedErrors(request, graphqlErrors);

    return body;
  }

  const rejectionError =
    request.tolerateFieldScopedErrors === true
      ? (graphqlErrors.find((error) => !isFieldScopedGraphqlError(error)) ?? graphqlErrors[0])
      : graphqlErrors[0];

  throw new GraphqlApiError(request, rejectionError, response.status, response.headers);
};

// oxlint-disable-next-line typescript/no-explicit-any
type ProcessGraphqlResponse = (request: ExecutedGraphqlApiRequest, response: Response) => Promise<any>;
const processGraphqlResponse: ProcessGraphqlResponse = async (request, response) => {
  const body = await getResponseBody(response);
  const graphqlErrors = isGraphqlResponseBody(body) && isGraphqlErrorArray(body.errors) ? body.errors : undefined;

  /*
    This is a temporary fix for system message issue. See more details: https://dev.azure.com/yammer/engineering/_workitems/edit/312266
    We will remove this once we have a proper BE fix.
  */
  if (graphqlErrors?.length === 1 && graphqlErrors[0].message === 'Group not found') {
    return body;
  }

  /*
    Allow partial responses when storylineOnlyForUsersInOfficeGroup fails to load. Temporary fix to unblock customer
    The group may have been deleted or is inaccessible, but we still want to load the rest of the settings.
  */
  if (graphqlErrors?.[0]?.path?.includes('storylineOnlyForUsersInOfficeGroup') && body.data?.settings?.storyline) {
    return body;
  }

  const toleratedResponse = processGraphqlErrors(request, body, response);

  if (toleratedResponse) {
    return toleratedResponse;
  }

  if (response.ok) {
    return body;
  }

  throw new GraphqlApiError(request, response.statusText, response.status, response.headers);
};

type InvokeApiRequest = <TResponse>(request: ExecutedGraphqlApiRequest) => Promise<ApiResponse<TResponse>>;
const graphqlApiRequest: InvokeApiRequest = async (request) => {
  try {
    const response = await executeRequest(request);
    const body = await processGraphqlResponse(request, response);

    return { body, headers: response.headers };
  } catch (error) {
    if (error instanceof GraphqlApiError) {
      throw error;
    }

    throw new GraphqlApiError(request, error);
  }
};

type InvokeGraphqlApiRequest = <TResponse>(request: GraphqlApiRequest<unknown>) => Promise<ApiResponse<TResponse>>;

export const graphqlApiRequestWithoutAutomaticPersistedQueries: InvokeGraphqlApiRequest = async (request) => {
  const {
    body: {
      query: { contents },
      ...bodyWithoutQuery
    },
    ...requestWithoutBody
  } = request;
  const requestWithQuery = {
    ...requestWithoutBody,
    body: {
      ...bodyWithoutQuery,
      query: contents,
    },
  };

  return graphqlApiRequest(requestWithQuery);
};

export const graphqlApiRequestWithAutomaticPersistedQueries: InvokeGraphqlApiRequest = async (request) => {
  const {
    body: {
      query: { contents, sha256Hash },
      extensions: originalExtensions,
      ...bodyWithoutQuery
    },
    ...requestWithoutBody
  } = request;
  const extensions = {
    ...originalExtensions,
    persistedQuery: {
      version: 1,
      sha256Hash,
    },
  };
  const requestWithoutQuery = {
    ...requestWithoutBody,
    body: {
      ...bodyWithoutQuery,
      extensions,
    },
  };
  const requestWithQuery = {
    ...requestWithoutBody,
    body: {
      ...bodyWithoutQuery,
      query: contents,
      extensions,
    },
  };

  try {
    return await graphqlApiRequest(requestWithoutQuery);
  } catch (error) {
    if (error.message === 'PersistedQueryNotFound') {
      return graphqlApiRequest(requestWithQuery);
    }

    throw error;
  }
};
